import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import { DialogActions, DialogContent, DialogTitle } from '../dialog/DialogTitle';
import { CustomScrollbarBox, StyledPayButton } from '../../utilities/styles/checkoutPage.style';

interface Holiday {
  date: string;
  day: string;
  time?: string;
}

interface Schedule {
  [day: string]: {
    timings: string;
  };
}

interface OpenHoursDialogProps {
  open: boolean;
  onClose: () => void;
  organization?: {
    name: string;
  };
  organizationSettings?: any;
  schedule: Schedule;
  formattedHolidays: Holiday[];
  formatDateForTimezone: (date: string) => string;
  contentHeight: any;
  classes?: any;
}

const OpenHoursDialog: React.FC<OpenHoursDialogProps> = ({
  open,
  onClose,
  organization,
  organizationSettings,
  schedule,
  formattedHolidays,
  formatDateForTimezone,
  contentHeight,
  classes = {},
}) => {
  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      className={classes.paymentDialog}
      disableEscapeKeyDown={true}
      hideBackdrop={true}
      PaperProps={{
        style: {
          border: "1px solid",
          borderColor:
            organizationSettings?.layoutColor &&
            lighten(organizationSettings?.layoutColor, 0.5),
          borderRadius: "12px",
          overflowX: "hidden",
          width: "660px",
        },
      }}
    >
      <DialogTitle
        id="customized-dialog-title"
        className={classes.dialogTitle}
        classes={{ closeButton: classes.closeButton }}
        sx={{
          p: "0px 24px",
          "& svg": {
            width: 25,
          },
        }}
      />
      <DialogContent style={{ height: contentHeight }}
              sx={{ p: 0 }}>
        <Box>
          <Typography
            sx={{
              mt: 4,
              mb: 2,
              textAlign: "center",
              fontSize: { xs: "20px" },
              fontFamily: "Poppins, Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 500,
            }}
          >
            {organization?.name} Hours of Operation
          </Typography>

          <Grid
            container={true}
            spacing={2}
            direction={"column"}
            width="90%"
            m="0px auto"
          >
            {Object?.entries(schedule)?.map(([day, { timings }]) => (
              <Grid item xs={12} sm={12} key={day}>
                <Grid container sx={{ height: { md: "35px" } }}>
                  <Grid item xs={12} sm={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily:
                            "Poppins, Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: { xs: "16px" },
                          color: "#000000",
                        }}
                      >
                        {day?.charAt(0)?.toUpperCase() + day?.slice(1)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily:
                            "Poppins, Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: { xs: "16px" },
                          color: "#000000",
                        }}
                      >
                        {timings}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Grid
            container={true}
            spacing={2}
            direction={"column"}
            width="90%"
            m="0px auto"
          >
            <Grid item xs={12} sm={12}>
              <Grid container sx={{ height: { md: "35px" } }}>
                <Grid item xs={12} sm={12}>
                  <Typography
                    sx={{
                      fontFamily:
                        "Poppins, Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 500,
                      fontSize: { xs: "20px" },
                      color: "#000000",
                    }}
                  >
                    Holidays:
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <CustomScrollbarBox>
              {formattedHolidays?.map((item: Holiday) => {
                const formattedDate = formatDateForTimezone(item?.date);
                return (
                  <Grid item xs={12} sm={12} mb={1} key={item.date}>
                    <Grid
                      container
                      sx={{
                        height: {
                          md: "35px",
                          padding: "12px 0px 12px 12px",
                        },
                      }}
                    >
                      <Grid item xs={12} sm={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Grid md={7}>
                            <Typography
                              sx={{
                                fontFamily:
                                  "Poppins, Roboto, Helvetica, Arial, sans-serif",
                                fontWeight: 400,
                                fontSize: { xs: "16px" },
                                color: "#000000",
                              }}
                            >
                              {item?.day}
                            </Typography>
                          </Grid>
                          <Grid md={7}>
                            <Typography
                              sx={{
                                fontFamily:
                                  "Poppins, Roboto, Helvetica, Arial, sans-serif",
                                fontWeight: 400,
                                fontSize: { xs: "16px" },
                                color: "#000000",
                              }}
                            >
                              {formattedDate}
                            </Typography>
                          </Grid>
                          <Grid md={6}>
                            <Typography
                              sx={{
                                fontFamily:
                                  "Poppins, Roboto, Helvetica, Arial, sans-serif",
                                fontWeight: 400,
                                fontSize: { xs: "16px" },
                                color: "#000000",
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.time ? item.time : "Closed"}
                            </Typography>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </CustomScrollbarBox>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            width: "100%",
            px: { xs: 0, sm: 4, md: 6, lg: 8 },
            pb: 2,
          }}
        >
          <StyledPayButton
            variant="contained"
            color="layoutColor"
            disableElevation
            sx={{
              backgroundColor: organizationSettings?.layoutColor,
              "&.MuiButton-root:hover": {
                backgroundColor: organizationSettings?.layoutColor,
              },
            }}
            onClick={onClose}
          >
            <Typography
              sx={{
                fontSize: { xs: "16px" },
                fontWeight: 600,
              }}
            >
              Close
            </Typography>
          </StyledPayButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default OpenHoursDialog;